import { fmtCurrency } from "../../../../utils/functions";

const TIER_ORDER = ["Starter", "Growth", "Scale"];

const TIER_META = {
  Scale: { inlineTag: "BEST VALUE" },
};

// Ported from PlansTab's buildAnnualFeatures — keep in sync if that changes.
export function buildAnnualFeatures(plan) {
  const f = [];
  if (plan?.candidateShortlistLimit != null)
    f.push(
      `${plan?.candidateShortlistLimit === -1 ? "Unlimited" : plan?.candidateShortlistLimit} candidate shortlists`,
    );
  if (plan?.activeJobPostsLimit != null && plan?.activeJobPostsLimit !== 0)
    f.push(
      `${plan?.activeJobPostsLimit === -1 ? "Unlimited" : plan?.activeJobPostsLimit} active job posts`,
    );
  if (plan?.emailSupport) f.push("Email support");
  if (plan?.prioritySupport) f.push("Priority support");
  if (plan?.teamCollaborationSeats > 0)
    f.push(
      `${plan?.teamCollaborationSeats === -1 ? "Unlimited" : plan?.teamCollaborationSeats} team seats`,
    );
  if (plan?.advancedAnalyticsDashboard) f.push("Advanced analytics dashboard");
  if (plan?.aiWorkforceInsights) f.push("AI workforce insights");
  if (plan?.dedicatedSuccessManager) f.push("Dedicated success manager");
  if (plan?.apiAccess) f.push("API access");
  if (plan?.customCreditVolume) f.push("Custom credit volume");
  if (plan?.hrisAtsIntegrations) f.push("HRIS / ATS integrations");
  if (plan?.whiteGloveOnboarding) f.push("White-glove onboarding");
  if (plan?.engineeringSupport) f.push("Engineering support");
  return f;
}

function matchActiveTier(activeSubscription) {
  if (!activeSubscription?.isActive || !activeSubscription?.planName)
    return null;
  const name = activeSubscription?.planName?.toLowerCase();
  return TIER_ORDER.find((tier) => name.includes(tier.toLowerCase())) || null;
}

function formatExpiry(isoString) {
  if (!isoString) return null;
  return new Date(isoString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function mapAnnualPlans(
  apiAnnualPlans,
  { currency, activeSubscription } = {},
) {
  const isNGN = currency === "NGN";

  // Same filter/sort as PlansTab's sortedAnnual, minus the enterprise tail-append (excluded for now)
  const sortedAnnual = apiAnnualPlans
    .filter((p) => !p?.isCustomPricing)
    .sort((a, b) => a?.priceNGN - b?.priceNGN);

  const activeTier = matchActiveTier(activeSubscription);
  const currentIndex = activeTier ? TIER_ORDER.indexOf(activeTier) : -1;

  return sortedAnnual.map((plan) => {
    const isCurrent = plan?.name === activeTier;
    const planIndex = TIER_ORDER?.indexOf(plan?.name);
    const displayPrice = isNGN ? plan?.priceNGN : plan?.priceUSD;

    let cta;
    if (isCurrent) {
      const expiry = formatExpiry(activeSubscription?.validityPeriod);
      cta = {
        label: expiry ? `Subscribed · until ${expiry}` : "Subscribed",
        variant: "subscribed",
      };
    } else if (currentIndex !== -1 && planIndex < currentIndex) {
      cta = {
        label: "Available at renewal",
        variant: "default",
        toastMessage:
          "Downgrades apply at renewal — your current plan runs its full term.",
      };
    } else {
      cta = {
        label: `Upgrade to ${plan?.name}`,
        variant: "default",
        toastMessage: `Upgrading to ${plan?.name} — ${fmtCurrency(displayPrice, currency)}/yr, ${plan?.creditUnits} credits upfront…`,
      };
    }

    return {
      id: plan?._id,
      planKind: "plan",
      featured: isCurrent,
      topBadge: isCurrent ? "YOUR PLAN" : undefined,
      tierLabel: plan?.name?.toUpperCase(),
      inlineTag: TIER_META[plan?.name]?.inlineTag,
      name: plan?.name,
      priceCurrency: isNGN ? "₦" : "$",
      priceValue: displayPrice.toLocaleString("en-US"),
      price: displayPrice,
      currency: plan?.currency,
      priceNGN: plan?.priceNGN,
      priceUSD: plan?.priceUSD,
      pricePeriod: "/yr",
      pricePerCredit: plan?.creditUnits,
      tagline: { type: "chip", text: `${plan?.creditUnits} credits upfront` },
      features: buildAnnualFeatures(plan),
      cta,
    };
  });
}
